/* eslint-disable */
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './StreamKeys.module.scss';
import { useGetStreamKeys } from '@/adapters/stream-keys';
import { useListRunsheetsByStreamKey } from '@/adapters/runsheet-v2';
import CopyToClipboard from '@/components/CopyToClipboard';
import PrimaryButton from '@/components/common/PrimaryButton';
import AddIcon from '@mui/icons-material/Add';
import { Typography } from '@mui/material';
import Loading from '@/components/Loading';
import Api from '../../adapters/api';
import DefaultButton from '../../components/common/DefaultButton';
import { UserContext } from '@/contexts/UserContext';
import debounce from 'lodash.debounce';
import { useListEvents } from '@/adapters/events-v2';
import ManageStreamKeyManagersDialog from './ManageStreamKeyManagersDialog';
import StreamKeyListItem from '@/entities/stream-key-list-item';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Tooltip, Box } from '@mui/material';
import { RUNSHEETS_URL } from '@/routePaths';

/**
 * Component to display runsheet association status for a stream key.
 * Shows "Yes" + count if at least one non-deleted runsheet exists.
 */
const RunsheetStatusIndicator: FC<{ streamKey: string }> = ({ streamKey }) => {
  const { data, isLoading } = useListRunsheetsByStreamKey(streamKey);

  if (isLoading) {
    return (
      <Typography variant="body2" color="textSecondary">
        Checking...
      </Typography>
    );
  }

  const count = data?.length ?? 0;
  if (count > 0) {
    const latest = data?.[0];
    return (
      <Tooltip
        title={`${count} runsheet${count === 1 ? '' : 's'}${latest ? ` — latest "${latest.title}"` : ''}`}
        arrow
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'success.main',
            cursor: 'help',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: '20px' }} />
          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
            {count}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Typography variant="body2" color="textSecondary">
      No
    </Typography>
  );
};

/* istanbul ignore file */

{
  /* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
}
const StreamKeys: FC = () => {
  const navigate = useNavigate();
  const { userState } = useContext(UserContext);
  const isManager = userState?.role === 'manager';
  const { data: streamKeyList = { items: [] }, isLoading } = useGetStreamKeys(userState?.role);
  const { items: streamKeys } = streamKeyList;
  const [name, setName] = useState<string>('');
  const [managingStreamKey, setManagingStreamKey] = useState<StreamKeyListItem | null>(null);

  // Fetch events for new features (admins only - managers get eventName from eos-app)
  const { data: events = [] } = useListEvents(isManager ? undefined : userState?.orgId);

  // Create a map of eventId to event name for display
  const eventNameMap = useMemo(
    () => new Map(events.map((event) => [event.id, event.name])),
    [events],
  );

  const debouncedApiCall = useCallback(
    debounce(async (value: string, id: string) => {
      const data = { id, name: value };
      await Api.post('/stream-keys', data);
    }, 800), // adjust debounce time as needed
    [],
  );

  const handleOnChange = async (value: string, id: string) => {
    setName(value);
    debouncedApiCall(value, id);
  };

  const handleOnReset = async (id: string) => {
    const data = {
      id: id,
    };
    await Api.post('/stream-keys/reset', data);
    window.location.reload();
  };
  const handleOnDelete = async (id: string) => {
    const data = {
      id: id,
    };
    await Api.delete(`/stream-keys/${id}`);
    window.location.reload();
  };

  return (
    <>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className={styles.pageTitle}>
            {isManager ? 'My Stream Keys' : 'Stream Keys'}
          </h1>
          {!isManager && (
            <DefaultButton
              onClick={() => navigate(RUNSHEETS_URL)}
              style={{ marginLeft: 'auto' }}
            >
              Manage Runsheets
            </DefaultButton>
          )}
        </div>
        <div className={styles.subTitles}>
          <p className={styles.pageDescription}>
            {isManager
              ? 'These are the stream keys assigned to you. Use them with your favorite streaming service.'
              : 'To use the stream keys with your favorite streaming service, copy and paste these links.'}
          </p>
          {!isManager && (
            <PrimaryButton type="button">
              <Typography
                sx={{
                  fontSize: { xs: '11px', md: '1rem', lg: '1rem' },
                  paddingLeft: '8px',
                  paddingRight: '5px',
                }}
              >
                Add Stream
              </Typography>
              <AddIcon />
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className={styles.table}>
        <div className={styles.tableTitle}>
          <div className={`${styles.tableItem}`}>Stream Name</div>
          <div className={`${styles.tableItem} ${styles.columnType}`}>Type</div>
          <div className={styles.tableItem}>Streaming Server</div>
          <div className={styles.tableItem}>Stream Key</div>
          <div className={`${styles.tableItem} ${styles.columnPlaybackUrl}`}>Playback URL</div>
          <div className={`${styles.tableItem} ${styles.columnRunsheet}`}>Runsheet</div>
          {!isManager && <div className={styles.tableItem}>Actions</div>}
        </div>
        {isLoading ? (
          <Loading />
        ) : streamKeys.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              {isManager
                ? 'No stream keys have been assigned to you yet.'
                : 'No stream keys found.'}
            </Typography>
          </div>
        ) : (
          streamKeys
            .filter((stream) => stream)
            .map((streamKey, index) => {
              const isDisabled = streamKey.isBlackListed;
              const eventLabel =
                streamKey.eventName ||
                (streamKey.eventId ? eventNameMap.get(streamKey.eventId) : undefined);

              return (
                <div
                  className={`${styles.tableBody} ${isDisabled ? styles.disabledRow : ''}`}
                  key={index}
                >
                  <div className={styles.tableBodyTop}>
                    <div className={`${styles.tableItem}`}>
                      {isManager ? (
                        <span>{streamKey.name ?? streamKey.id}</span>
                      ) : (
                        <input
                          type="text"
                          defaultValue={streamKey.name ?? streamKey.id}
                          onChange={(_) => handleOnChange(_.target.value, streamKey.id)}
                          disabled={isDisabled}
                        />
                      )}
                    </div>
                    <div className={`${styles.tableItem} ${styles.columnType}`}>
                      {streamKey.type || 'RTMP'}
                    </div>

                    <div className={styles.tableItem}>
                      <CopyToClipboard text={streamKey.ingestServer} enabled={isDisabled}>
                        <input type="text" value={streamKey.ingestServer} disabled={isDisabled} readOnly />
                      </CopyToClipboard>
                    </div>
                    <div className={styles.tableItem}>
                      <CopyToClipboard text={streamKey.streamKey} enabled={isDisabled}>
                        <input type="text" value={streamKey.streamKey} disabled={isDisabled} readOnly />
                      </CopyToClipboard>
                    </div>
                    <div className={`${styles.tableItem} ${styles.columnPlaybackUrl}`}>
                      <CopyToClipboard text={streamKey.playbackUrl} enabled={isDisabled}>
                        <input type="text" value={streamKey.playbackUrl} disabled={isDisabled} readOnly />
                      </CopyToClipboard>
                    </div>
                    <div className={`${styles.tableItem} ${styles.columnRunsheet}`}>
                      <RunsheetStatusIndicator streamKey={streamKey.streamKey} />
                    </div>
                    {!isManager && (
                      <div className={`${styles.tableItem} ${styles.tableBtn}`}>
                        <DefaultButton
                          size="small"
                          style={{ marginLeft: '5px' }}
                          onClick={() => setManagingStreamKey(streamKey)}
                          disabled={isDisabled}
                        >
                          Managers
                        </DefaultButton>
                        <DefaultButton
                          size="small"
                          style={{ marginLeft: '5px' }}
                          onClick={() => handleOnReset(streamKey.id)}
                          disabled={isDisabled}
                        >
                          Reset
                        </DefaultButton>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
        )}
      </div>
      {!isManager && (
        <ManageStreamKeyManagersDialog
          open={!!managingStreamKey}
          streamKey={managingStreamKey}
          onClose={() => setManagingStreamKey(null)}
        />
      )}
    </>
  );
};

export default StreamKeys;
